import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../config/api";
import { 
  Globe, Swords, AlertTriangle, ShieldAlert, Receipt, 
  UserCheck, TrendingUp, BarChart3, RefreshCw, ExternalLink, Clock, ChevronDown, ChevronUp
} from "lucide-react";

const CATEGORY_META = {
  geopolitics: { icon: Globe, color: "text-blue-400", bg: "bg-blue-500/10", border: "border-blue-500/30", accent: "#3b82f6" },
  wars: { icon: Swords, color: "text-red-400", bg: "bg-red-500/10", border: "border-red-500/30", accent: "#ef4444" },
  global_tensions: { icon: AlertTriangle, color: "text-amber-400", bg: "bg-amber-500/10", border: "border-amber-500/30", accent: "#f59e0b" },
  trade_wars: { icon: ShieldAlert, color: "text-orange-400", bg: "bg-orange-500/10", border: "border-orange-500/30", accent: "#f97316" },
  tariffs: { icon: Receipt, color: "text-purple-400", bg: "bg-purple-500/10", border: "border-purple-500/30", accent: "#a855f7" },
  insider_trading: { icon: UserCheck, color: "text-emerald-400", bg: "bg-emerald-500/10", border: "border-emerald-500/30", accent: "#10b981" },
  stock_market: { icon: TrendingUp, color: "text-cyan-400", bg: "bg-cyan-500/10", border: "border-cyan-500/30", accent: "#06b6d4" },
  global_stock_market: { icon: BarChart3, color: "text-indigo-400", bg: "bg-indigo-500/10", border: "border-indigo-500/30", accent: "#6366f1" },
};

const formatTimeAgo = (published) => {
  if (!published) return "";
  try {
    const date = new Date(published);
    const now = new Date();
    const diffMs = now - date;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return "just now";
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.floor(diffHr / 24);
    return `${diffDay}d ago`;
  } catch {
    return "";
  }
};

const NewsCategory = ({ category }) => {
  const [expanded, setExpanded] = useState(true);
  const meta = CATEGORY_META[category.category] || CATEGORY_META.geopolitics;
  const Icon = meta.icon;
  const articles = category.articles || [];

  return (
    <div data-testid={`news-category-${category.category}`} className={`rounded-xl border ${meta.border} ${meta.bg} overflow-hidden`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-white/5 transition-colors"
        data-testid={`news-toggle-${category.category}`}
      >
        <div className="flex items-center gap-3">
          <Icon className={`w-5 h-5 ${meta.color}`} />
          <h3 className={`text-base font-semibold ${meta.color}`}>{category.label}</h3>
          <span className="text-xs text-slate-500 bg-slate-800 px-2 py-0.5 rounded-full">
            {articles.length}
          </span>
        </div>
        {expanded ? (
          <ChevronUp className="w-4 h-4 text-slate-500" />
        ) : (
          <ChevronDown className="w-4 h-4 text-slate-500" />
        )}
      </button>

      {expanded && (
        <div className="px-5 pb-4 space-y-1">
          {articles.length === 0 ? (
            <p className="text-sm text-slate-500 italic py-2">No articles found for this category</p>
          ) : (
            articles.map((article, idx) => (
              <a
                key={idx}
                href={article.link}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-3 py-2.5 px-3 -mx-3 rounded-lg hover:bg-white/5 transition-colors group"
                data-testid={`news-article-${category.category}-${idx}`}
              >
                <div
                  className="w-1 h-1 rounded-full mt-2.5 flex-shrink-0"
                  style={{ backgroundColor: meta.accent }}
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-200 leading-snug group-hover:text-white transition-colors line-clamp-2">
                    {article.title}
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-slate-500">{article.source}</span>
                    {article.published && (
                      <>
                        <span className="text-slate-700 text-xs">&middot;</span>
                        <span className="text-xs text-slate-600 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatTimeAgo(article.published)}
                        </span>
                      </>
                    )}
                  </div>
                </div>
                <ExternalLink className="w-3.5 h-3.5 text-slate-600 opacity-0 group-hover:opacity-100 mt-1 flex-shrink-0 transition-opacity" />
              </a>
            ))
          )}
        </div>
      )}
    </div>
  );
};

const TodaysNewsPage = () => {
  const [newsData, setNewsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);

  const fetchNews = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/news/today`, { timeout: 30000 });
      if (res.data && res.data.categories) {
        setNewsData(res.data);
        setLastUpdate(new Date());
      }
    } catch (err) {
      console.error("Error fetching news:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNews();
    const interval = setInterval(fetchNews, 300000); // Refresh every 5 min
    return () => clearInterval(interval);
  }, [fetchNews]);

  const categories = newsData?.categories || [];
  const totalArticles = newsData?.total_articles || 0;

  return (
    <div data-testid="todays-news-page" className="max-w-5xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Today's News</h1>
          <p className="text-sm text-slate-400 mt-1">
            {totalArticles > 0 ? `${totalArticles} articles` : "Loading"} across {categories.length || 8} categories
            {lastUpdate && (
              <span className="ml-2 text-slate-600">
                &middot; Updated {lastUpdate.toLocaleTimeString()}
              </span>
            )}
          </p>
        </div>
        <button
          onClick={fetchNews}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-2 text-sm text-slate-400 hover:text-white bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-50"
          data-testid="news-refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {/* Loading skeleton */}
      {loading && !newsData && (
        <div className="space-y-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="rounded-xl border border-slate-800 bg-slate-900/50 p-5 animate-pulse">
              <div className="h-5 w-48 bg-slate-800 rounded mb-4" />
              <div className="space-y-3">
                <div className="h-4 w-full bg-slate-800/50 rounded" />
                <div className="h-4 w-5/6 bg-slate-800/50 rounded" />
                <div className="h-4 w-4/6 bg-slate-800/50 rounded" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* News Categories */}
      {categories.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {categories.map((cat) => (
            <NewsCategory key={cat.category} category={cat} />
          ))}
        </div>
      )}

      {/* No data state */}
      {!loading && categories.length === 0 && (
        <div className="text-center py-16">
          <Globe className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <p className="text-slate-400">Unable to load news. Please try again.</p>
          <button
            onClick={fetchNews}
            className="mt-4 px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg text-sm transition-colors"
          >
            Retry
          </button>
        </div>
      )}
    </div>
  );
};

export default TodaysNewsPage;
